/**
 * Seção de Integração CRM no EventTypeGeneralTab
 * 
 * Permite configurar criação automática de negócio CRM
 * ao confirmar agendamento via link genérico.
 * 
 * Componentes padronizados:
 * - Workspace: Popover + Command (padrão do modal de metas)
 * - Pipeline: Popover + Command (padrão do modal de edição de deal)
 * - Fase: PhaseSelector reutilizável
 * - Vendedor: SellerSelector reutilizável
 * 
 * RELEASE: 2026-02-20
 * VERSION: 2.0.0 - Padronização de selects + campo agendamentos simultâneos
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Briefcase, Check, ChevronsUpDown, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { LEAD_SOURCES, LEAD_SOURCE_ICONS } from '@/lib/lead-sources';
import { createLogger } from '@/lib/logger';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { PhaseSelector } from '@/components/crm/PhaseSelector';

const logger = createLogger('CrmIntegrationSection');

/** Interface para filas de distribuição */
interface DistributionQueue {
  id: string;
  name: string;
  distribution_method: string;
  context_type: string;
}

/** Props do formulário CRM */
export interface CrmIntegrationFormData {
  crm_auto_create_deal: boolean;
  crm_workspace_id: string | null;
  crm_pipeline_id: string | null;
  crm_phase_id: string | null;
  crm_source: string | null;
  crm_seller_assignment_mode: string;
  crm_seller_id: string | null;
  crm_distribution_queue_id: string | null;
}

interface CrmIntegrationSectionProps {
  /** Dados atuais do formulário CRM */
  form: CrmIntegrationFormData;
  /** Callback para atualizar dados do formulário */
  onChange: (updates: Partial<CrmIntegrationFormData>) => void;
  /** Valor de agendamentos simultâneos por slot */
  maxConcurrentBookings: number;
  /** Callback para atualizar agendamentos simultâneos */
  onMaxConcurrentChange: (value: number) => void;
}

export function CrmIntegrationSection({ form, onChange, maxConcurrentBookings, onMaxConcurrentChange }: CrmIntegrationSectionProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estados dos popovers
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);

  // Hierarquia de workspaces e pipelines
  const { workspaces, isLoading: loadingWorkspaces } = useCrmWorkspacesHierarchy();

  // Filas de distribuição (context_type 'crm' ou 'all')
  const { data: queues = [], isLoading: loadingQueues } = useQuery({
    queryKey: ['distribution-queues-crm', tenantId],
    queryFn: async (): Promise<DistributionQueue[]> => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, context_type')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .in('context_type', ['crm', 'all'])
        .order('name');

      if (error) {
        logger.error('Erro ao buscar filas de distribuição', error);
        return [];
      }
      return data || [];
    },
    enabled: !!tenantId && form.crm_seller_assignment_mode === 'distribution_queue',
  });

  // Pipelines filtrados pelo workspace selecionado
  const selectedWorkspace = workspaces.find(w => w.id === form.crm_workspace_id);
  const pipelines = selectedWorkspace?.pipelines || [];

  // Pipeline selecionado (para exibir nome)
  const selectedPipeline = pipelines.find(p => p.id === form.crm_pipeline_id);

  // Limpar campos dependentes quando workspace muda
  const handleWorkspaceChange = (workspaceId: string) => {
    if (workspaceId === form.crm_workspace_id) {
      setWorkspaceOpen(false);
      return;
    }
    onChange({
      crm_workspace_id: workspaceId,
      crm_pipeline_id: null,
      crm_phase_id: null,
    });
    setWorkspaceOpen(false);
  };

  // Limpar fase quando pipeline muda
  const handlePipelineChange = (pipelineId: string) => {
    if (pipelineId === form.crm_pipeline_id) {
      setPipelineOpen(false);
      return;
    }
    onChange({
      crm_pipeline_id: pipelineId,
      crm_phase_id: null,
    });
    setPipelineOpen(false);
  };

  // Limpar campos de atribuição quando modo muda
  const handleAssignmentModeChange = (mode: string) => {
    onChange({
      crm_seller_assignment_mode: mode,
      crm_seller_id: mode === 'manual' ? form.crm_seller_id : null,
      crm_distribution_queue_id: mode === 'distribution_queue' ? form.crm_distribution_queue_id : null,
    });
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          Integração CRM
        </CardTitle>
        <CardDescription>
          Crie automaticamente um negócio no CRM quando um agendamento for confirmado pelo link genérico
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle principal */}
        <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
          <div>
            <p className="font-medium text-sm text-foreground">
              Criar Negócio CRM ao confirmar agendamento
            </p>
            <p className="text-xs text-muted-foreground">
              Aplica-se somente a agendamentos pelo link genérico (sem vínculo prévio com negócio)
            </p>
          </div>
          <Switch
            checked={form.crm_auto_create_deal}
            onCheckedChange={(v) => onChange({ crm_auto_create_deal: v })}
          />
        </div>

        {/* Campos condicionais - só exibe quando toggle ativo */}
        {form.crm_auto_create_deal && (
          <div className="space-y-4 pl-1 border-l-2 border-primary/20 ml-2">
            <div className="pl-4 space-y-4">

              {/* Linha 1: Workspace + Pipeline (2 colunas) */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Workspace - Popover + Command (padrão modal de metas) */}
                <div className="space-y-2">
                  <Label>Workspace <span className="text-destructive">*</span></Label>
                  <Popover open={workspaceOpen} onOpenChange={setWorkspaceOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={workspaceOpen}
                        className="w-full justify-between font-normal"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className={cn("truncate", !selectedWorkspace && "text-muted-foreground")}>
                            {selectedWorkspace?.name || 'Selecione o workspace'}
                          </span>
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandList>
                          <CommandEmpty>Nenhum workspace encontrado.</CommandEmpty>
                          <CommandGroup>
                            {workspaces.map((ws) => (
                              <CommandItem
                                key={ws.id}
                                value={ws.name}
                                onSelect={() => handleWorkspaceChange(ws.id)}
                              >
                                <div className="flex items-center gap-2 flex-1">
                                  <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                  <span className="flex-1 truncate">{ws.name}</span>
                                </div>
                                <Check
                                  className={cn(
                                    'ml-2 h-4 w-4',
                                    form.crm_workspace_id === ws.id ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Pipeline - Popover + Command (padrão modal de edição do deal) */}
                {form.crm_workspace_id ? (
                  <div className="space-y-2">
                    <Label>Pipeline <span className="text-destructive">*</span></Label>
                    <Popover open={pipelineOpen} onOpenChange={setPipelineOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={pipelineOpen}
                          className="w-full justify-between font-normal"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className={cn("truncate", !selectedPipeline && "text-muted-foreground")}>
                              {selectedPipeline?.name || 'Selecione o pipeline'}
                            </span>
                          </div>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandList>
                            <CommandEmpty>Nenhum pipeline encontrado.</CommandEmpty>
                            <CommandGroup>
                              {pipelines.map((pip) => (
                                <CommandItem
                                  key={pip.id}
                                  value={pip.name}
                                  onSelect={() => handlePipelineChange(pip.id)}
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <span className="flex-1 truncate">{pip.name}</span>
                                  </div>
                                  <Check
                                    className={cn(
                                      'ml-2 h-4 w-4',
                                      form.crm_pipeline_id === pip.id ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                ) : <div /> /* Placeholder para manter grid */}
              </div>

              {/* Linha 2: Fase + Origem (2 colunas) */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Fase - PhaseSelector reutilizável (padrão modal de edição do deal) */}
                <div className="space-y-2">
                  <Label>Fase <span className="text-destructive">*</span></Label>
                  {form.crm_pipeline_id ? (
                    <>
                      <PhaseSelector
                        pipelineId={form.crm_pipeline_id}
                        value={form.crm_phase_id}
                        onSelect={(v) => onChange({ crm_phase_id: v })}
                        excludeFinalPhases={true}
                        placeholder="Selecione a fase"
                      />
                      <p className="text-xs text-muted-foreground">
                        Fases de ganho/perda são excluídas automaticamente
                      </p>
                    </>
                  ) : (
                    <Button variant="outline" disabled className="w-full justify-between font-normal text-muted-foreground">
                      Selecione o pipeline primeiro
                    </Button>
                  )}
                </div>

                {/* Origem do lead */}
                <div className="space-y-2">
                  <Label>Origem <span className="text-destructive">*</span></Label>
                  <Select
                    value={form.crm_source || ''}
                    onValueChange={(v) => onChange({ crm_source: v || null })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map((source) => {
                        const Icon = LEAD_SOURCE_ICONS[source.id];
                        return (
                          <SelectItem key={source.id} value={source.id}>
                            <div className="flex items-center gap-2">
                              {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                              {source.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Linha 3: Atribuição vendedor + Vendedor/Fila (2 colunas) */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Modo de atribuição do vendedor */}
                <div className="space-y-2">
                  <Label>Atribuição do vendedor</Label>
                  <Select
                    value={form.crm_seller_assignment_mode}
                    onValueChange={handleAssignmentModeChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      <SelectItem value="manual">Manual (selecionar vendedor)</SelectItem>
                      <SelectItem value="distribution_queue">Fila de distribuição</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Vendedor manual - SellerSelector reutilizável (padrão modal de metas) */}
                {form.crm_seller_assignment_mode === 'manual' && (
                  <div className="space-y-2">
                    <Label>Vendedor <span className="text-destructive">*</span></Label>
                    <SellerSelector
                      value={form.crm_seller_id}
                      onSelect={(v) => onChange({ crm_seller_id: v })}
                      placeholder="Selecione o vendedor"
                    />
                  </div>
                )}

                {/* Fila de distribuição */}
                {form.crm_seller_assignment_mode === 'distribution_queue' && (
                  <div className="space-y-2">
                    <Label>Fila de distribuição <span className="text-destructive">*</span></Label>
                    <Select
                      value={form.crm_distribution_queue_id || ''}
                      onValueChange={(v) => onChange({ crm_distribution_queue_id: v || null })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a fila" />
                      </SelectTrigger>
                      <SelectContent>
                        {queues.map((queue) => (
                          <SelectItem key={queue.id} value={queue.id}>
                            {queue.name} ({queue.distribution_method})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Agendamentos simultâneos por horário - último campo dentro da seção CRM */}
              <div className="space-y-2 pt-2 border-t border-border/30">
                <Label>Agendamentos simultâneos por horário</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  className="sm:max-w-[200px]"
                  value={maxConcurrentBookings}
                  onChange={(e) =>
                    onMaxConcurrentChange(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Quantidade de agendamentos permitidos no mesmo horário. Ex: 3 vendedores = 3 pessoas podem agendar às 12:00.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
